import { GetState, SetState } from 'zustand';
import produce from 'immer';
import { StoreSlice } from '../../utils/store-slice';
import { ILogger } from '../concepts/logger';
import { IGame } from '../concepts/game';
import {
  IToDos,
  ReadInfoPayload,
  ToDoDescription,
  VotingPayload,
} from '../concepts/to-dos';
import { ILocations } from '../concepts/locations';
import { IDocs } from '../concepts/docs';
import { IAvatar } from '../concepts/avatar';
import { IVoting } from '../concepts/voting';
import { remote } from '../../../remote';

export const create: StoreSlice<IToDos, ILogger & IGame> = (set, get) => {
  return {
    thingsToDo: [],
    goForwardLabel: 'Vai alla prossima cosa da fare',

    clearToDos() {
      set({ thingsToDo: [] });
    },

    appendToDo(toDo) {
      set(
        produce((s: IToDos) => {
          s.thingsToDo.push(toDo);
        })
      );

      get().syncToDos();
    },

    completeFirstToDo() {
      if (get().thingsToDo.length === 0) {
        console.log('📋 [ToDo] completeFirstToDo called but no todos');
        return;
      }

      const todoId = get().thingsToDo[0].id;
      console.log(`📋 [ToDo] completeFirstToDo called for: «${todoId}»`);
      get().log(`completing first todo: «${todoId}»`);

      const todo = createTodo(get().thingsToDo[0]);
      const isDone = todo.isDone(get);

      console.log(`📋 [ToDo] Todo «${todoId}» isDone:`, isDone);

      if (isDone) {
        console.log(`📋 [ToDo] Completing todo «${todoId}»`);
        set(
          produce((s: IToDos) => {
            s.thingsToDo.shift();
          })
        );

        todo.onComplete(get, set);

        get().putFocusOnNextToDo();
      }

      get().syncToDos();
    },

    completeAllToDos() {
      const toDos = get().thingsToDo.map(createTodo);

      set(
        produce((s: IToDos) => {
          s.thingsToDo = s.thingsToDo.filter((t, i) => !toDos[i].isDone(get));
        })
      );

      toDos.filter(t => t.isDone).forEach(t => t.onComplete(get, set));

      get().putFocusOnNextToDo();

      get().syncToDos();
    },

    putFocusOnNextToDo() {
      if (get().thingsToDo.length === 0) return;
      createTodo(get().thingsToDo[0]).onFocus(get, set);
    },

    nextLocationToVisit() {
      if (get().thingsToDo.length === 0) return;
      return get().thingsToDo[0].locationToVisit;
    },

    currentToDoNudge() {
      if (get().thingsToDo.length === 0) return;
      return get().thingsToDo[0].nudge;
    },

    syncToDos() {
      remote.syncToDos(get().gameId, get().gamePlayId, get().thingsToDo);
    },
  };
};

interface IToDo {
  onFocus(get: GetState<any>, set: SetState<any>): void;
  isDone(get: GetState<any>): boolean;
  onComplete(get: GetState<any>, set: SetState<any>): void;
}

const createTodo = (description: ToDoDescription): IToDo => {
  switch (description.type) {
    case 'read-info':
      return readInfo(description);

    case 'read-all-docs':
      return readAllDocs(description);

    case 'select-avatar':
      return selectAvatar(description);

    case 'vote':
      return vote(description);

    default:
      return mockTodo(description);
  }
};

const readInfo = (todo: ToDoDescription<ReadInfoPayload>): IToDo => ({
  onFocus(get, set: SetState<IGame>) {
    // EMIT EVENT INTRO STAGE
  },
  isDone(get: GetState<ILocations>) {
    return get().isLocationVisited(todo.locationToVisit);
  },
  onComplete() {},
});

const readAllDocs = (todo: ToDoDescription): IToDo => ({
  onFocus(get, set: SetState<IGame>) {
    const currentPhase = get().currentPhase;

    if (!currentPhase) return;
  },
  isDone(get: GetState<IDocs>) {
    return get().areAllDocsRead();
  },
  onComplete() {},
});

const selectAvatar = (todo: ToDoDescription): IToDo => ({
  onFocus(get: GetState<ILocations>) {
    console.log('📋 [ToDo] selectAvatar onFocus - opening avatar location');
    get().openLocation('avatar');
  },
  isDone(get: GetState<IAvatar>) {
    const allSelected = get().areAllAvatarsSelected();
    console.log('📋 [ToDo] selectAvatar isDone check:', allSelected);
    return allSelected;
  },
  onComplete(get: GetState<IGame>) {
    console.log('📋 [ToDo] selectAvatar onComplete - changing phase to intro-2 for game:', get().gameId);
    remote.startGamePhase(get().gameId, 'intro-2')
      .then(() => {
        console.log('✅ [ToDo] Phase change to intro-2 succeeded');
      })
      .catch((error) => {
        console.error('❌ [ToDo] Phase change to intro-2 failed:', error);
      });
  },
});

const vote = (todo: ToDoDescription<VotingPayload>): IToDo => ({
  onFocus(get: GetState<ILocations>) {
    get().openLocation('avatar');
  },
  isDone(get: GetState<IVoting & ILogger>) {
    return todo.payload.sessions.every(s => {
      get().log(`checking ${s}: ${get().isSessionEnded(s, Date.now())}`);
      return get().isSessionEnded(s, Date.now());
    });
  },
  onComplete(get: GetState<IGame>) {
    remote.startGamePhase(get().gameId, todo.payload.gotoPhase);
  },
});

const mockTodo = (todo: ToDoDescription): IToDo => ({
  onFocus(get: GetState<ILogger>, set: SetState<ILogger>) {
    get().log(`mock todo focused, ${todo.type}/${todo.id}`);
  },
  isDone(get: GetState<ILogger>) {
    get().log(`mock todo checked, ${todo.type}/${todo.id}`);
    return false;
  },
  onComplete() {},
});
